import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Trophy, Swords, Target, Calendar, ExternalLink, Plus, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GameIcon } from '@/components/ui/GameIcon';
import { getGameName } from '@/lib/gameConfig';
import { api, type Wager } from '@/lib/api';
import { formatCurrency, formatRelativeTime } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';

function computeStats(wagers: Wager[], userId: string) {
  const settled = wagers.filter((w) =>
    ['settled', 'ready_to_collect'].includes(w.status)
  );
  const wins = settled.filter((w) => w.winner?.id === userId).length;
  const losses = settled.filter(
    (w) => w.winner && w.winner.id !== userId
  ).length;
  const draws = settled.length - wins - losses;
  const totalWagered = settled.reduce(
    (sum, w) => sum + parseFloat(w.amount || '0'),
    0
  );
  const totalWon = settled
    .filter((w) => w.winner?.id === userId)
    .reduce((sum, w) => sum + parseFloat(w.amount || '0') * 2, 0);
  const winRate =
    wins + losses > 0 ? Math.round((wins / (wins + losses)) * 100) : 0;

  return {
    totalWagers: wagers.length,
    settled: settled.length,
    wins,
    losses,
    draws,
    totalWagered,
    totalWon,
    netProfit: totalWon - totalWagered,
    winRate,
  };
}

export function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);

  const isOwnProfile = !id || id === 'me' || id === currentUser?.id;

  // Own profile: use existing endpoints
  const { data: ownAccounts } = useQuery({
    queryKey: ['game-accounts'],
    queryFn: () => api.getGameAccounts(),
    enabled: isOwnProfile,
  });

  const { data: ownWagers } = useQuery({
    queryKey: ['my-wagers-all'],
    queryFn: () => api.getMyWagers(),
    enabled: isOwnProfile,
  });

  // Other user's profile: use public profile endpoint
  const { data: publicProfile } = useQuery({
    queryKey: ['user-profile', id],
    queryFn: () => api.getUserProfile(id!),
    enabled: !isOwnProfile && !!id,
  });

  // Unify data for rendering
  const userId = isOwnProfile ? (currentUser?.id || '') : (id || '');

  const accounts = isOwnProfile ? ownAccounts : publicProfile?.gameAccounts;

  const stats = isOwnProfile
    ? computeStats(ownWagers || [], userId)
    : (() => {
        const s = publicProfile?.stats;
        if (!s) return { totalWagers: 0, settled: 0, wins: 0, losses: 0, draws: 0, totalWagered: 0, totalWon: 0, netProfit: 0, winRate: 0 };
        return { ...s, settled: s.wins + s.losses + s.draws };
      })();

  const recentWagers: Wager[] = isOwnProfile
    ? (ownWagers || [])
        .filter((w) => ['settled', 'ready_to_collect', 'cancelled', 'voided'].includes(w.status))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5)
    : (publicProfile?.recentWagers || []).map((w) => ({
        ...w,
        wagerType: 'match_winner',
        visibility: 'public',
      })) as Wager[];

  const displayName = isOwnProfile
    ? (currentUser?.displayName || 'User')
    : (publicProfile?.user.displayName || 'User');
  const avatarUrl = isOwnProfile
    ? currentUser?.avatarUrl
    : (publicProfile?.user.avatarUrl ?? undefined);
  const joinedAt = isOwnProfile
    ? currentUser?.createdAt
    : publicProfile?.user.createdAt;

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <Avatar className="h-24 w-24">
              <AvatarImage src={avatarUrl} />
              <AvatarFallback className="text-3xl">
                {displayName[0]?.toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-center sm:text-left">
              <h1 className="font-heading text-2xl font-bold">{displayName}</h1>
              <div className="mt-1 flex flex-wrap items-center justify-center gap-2 text-sm text-muted-foreground sm:justify-start">
                <Calendar className="h-4 w-4" />
                <span>
                  {joinedAt
                    ? `Joined ${formatRelativeTime(joinedAt)}`
                    : 'Member'}
                </span>
              </div>
            </div>
            {!isOwnProfile && id && (
              <Button
                variant="gradient"
                className="gap-2"
                onClick={() => navigate(`/wagers/create?opponent=${id}&opponentName=${encodeURIComponent(displayName)}`)}
              >
                <Swords className="h-4 w-4" />
                Challenge
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardContent className="p-4 text-center">
              <Swords className="mx-auto mb-2 h-8 w-8 text-primary" />
              <p className="font-heading text-2xl font-bold">{stats.totalWagers}</p>
              <p className="text-sm text-muted-foreground">Challenges</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-4 text-center">
              <Trophy className="mx-auto mb-2 h-8 w-8 text-yellow-500" />
              <p className="font-heading text-2xl font-bold">
                {stats.settled > 0 ? `${stats.winRate}%` : '—'}
              </p>
              <p className="text-sm text-muted-foreground">Win Rate</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardContent className="p-4 text-center">
              <Target className="mx-auto mb-2 h-8 w-8 text-green-500" />
              <p className="font-heading text-2xl font-bold">
                {stats.wins}-{stats.losses}
              </p>
              <p className="text-sm text-muted-foreground">Record</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardContent className="p-4 text-center">
              <TrendingUp className="mx-auto mb-2 h-8 w-8 text-primary" />
              <p className="font-heading text-2xl font-bold">
                {stats.settled > 0
                  ? `${stats.netProfit >= 0 ? '+' : ''}${formatCurrency(stats.netProfit)}`
                  : '—'}
              </p>
              <p className="text-sm text-muted-foreground">Net Profit</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Linked Game Accounts */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Linked Games</CardTitle>
          {isOwnProfile && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => navigate('/settings')}
            >
              <Plus className="h-4 w-4" />
              Link Account
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {accounts && accounts.length > 0 ? (
            accounts.map((account, i) => (
              <motion.div
                key={account.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 rounded-xl border p-4"
              >
                <GameIcon gameCode={account.gameCode} size="lg" />
                <div className="flex-1">
                  <p className="font-medium">{'gameUsername' in account ? (account.gameUsername || account.username) : account.username}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{getGameName(account.gameCode)}</span>
                    {account.rating && (
                      <Badge variant="secondary">{account.rating}</Badge>
                    )}
                    {account.rank && <Badge variant="outline">{account.rank}</Badge>}
                  </div>
                </div>
                {'profileUrl' in account && account.profileUrl && (
                  <Button variant="ghost" size="icon" asChild>
                    <a href={account.profileUrl as string} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </motion.div>
            ))
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">No game accounts linked yet</p>
              {isOwnProfile && (
                <Button
                  variant="link"
                  className="mt-2"
                  onClick={() => navigate('/settings')}
                >
                  Link your first game
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Challenges */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Challenges</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {recentWagers.length > 0 ? (
            recentWagers.map((wager, i) => {
              const isWin = wager.winner?.id === userId;
              const isDraw = !wager.winner;
              const opponent =
                wager.creator?.id === userId
                  ? wager.opponent
                  : wager.creator;
              const amount = parseFloat(wager.amount || '0');

              return (
                <motion.div
                  key={wager.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center justify-between rounded-xl border p-4 cursor-pointer hover:bg-accent transition-colors"
                  onClick={() => navigate(`/wagers/${wager.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <GameIcon gameCode={wager.gameCode} size="sm" />
                    <div>
                      <p className="font-medium">
                        vs {opponent?.displayName || 'Unknown'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatRelativeTime(wager.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {wager.status === 'cancelled' || wager.status === 'voided' ? (
                      <Badge variant="secondary">Cancelled</Badge>
                    ) : isDraw ? (
                      <Badge variant="secondary">Draw</Badge>
                    ) : (
                      <Badge variant={isWin ? 'success' : 'destructive'}>
                        {isWin ? '+' : '-'}{formatCurrency(amount)}
                      </Badge>
                    )}
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">No completed challenges yet</p>
              <Button
                variant="link"
                className="mt-2"
                onClick={() => navigate('/wagers/create')}
              >
                Create your first challenge
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
